import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subject} from 'rxjs';
import {
    ALGORITHM_CONDO_OLD,
    ALGORITHM_FANO,
    ALGORITHM_NO_ALGORITHM,
    ECCacvRedistributionMode,
    ECEconomicsCappedVAIRedistributionSettings,
    ECEconomicsVAIRedistributionSettings,
    ECPlantTipMode,
    ECPlantTipType,
    ECSurplusPremiumRateScope
} from 'smag-commons/dto/eco/community';
import {
    ALGORITHM_CAPPED_VAI,
    ALGORITHM_MIXED,
    ALGORITHM_THOUSANDTH,
    ALGORITHM_VAI,
    ECEconomicsFanoRedistributionSettings,
    ECEconomicsRedistributedResponse
} from 'smag-commons/dto/eco';
import {Moment} from 'moment-timezone';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {MsgBoxService} from '../../../../shared/msg-box.service';
import {I18NService} from '../../../../i18n/i18n.service';
import {SmagApiService} from '../../../../api/smag-api.service';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {downloadBlob, isDefined} from '../../../../commons/commons';
import {EC_FILE_MIXED, EC_FILE_THOUSANDTHS} from 'smag-commons/cfg/hierarchy/ec';
import {HierarchyEntity} from '../../../../shared/hierarchy.service';
import {
    CACV_STRING,
    isUpdatable,
    isViewable,
    limitDecimals,
    sanityFromTimeActivationDate,
    sanityToTime,
    validateFileSize
} from '../../../../shared/ec-economics';
import {BillingPeriod, BillingPeriodConfig} from '../../billing-filter/ec-billing-filter.component';
import {ECEconomicsOldCondoRedistributionSettings} from 'smag-commons/src/dto/eco/community';


interface Template {
    name: string;
    file: File;
}

export interface SelectedAlgorithm {
    type: string;
    template?: Template;
    fixedPart?: number;
    splitConsumers?: number;
    globalCap: number;
    globalCapEnabled: boolean;
    willNotRedistribute?: boolean;
    isThousandthsAlgo?: boolean;
    foundersPercentage?: number;
    socialFundPercentage?: number;
    plantsPercentage?: number;
    tipFee: number;
    withdrawalFee: number;
    tipBenefit: number;
    withdrawalBenefit: number;
    cacvBenefit: number;
}

const MAX_FILE_MB = 2;
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

@Component({
    selector: 'app-ec-algorithm',
    templateUrl: './ec-algorithm.component.html',
    styleUrls: ['./ec-algorithm.component.scss']
})
export class ECAlgorithmComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private registeredControls = false;
    private selectedAlgorithm: SelectedAlgorithm = null;

    @Input() date: Moment;
    @Input() response: ECEconomicsRedistributedResponse;
    @Input() availableAlgorithms: string[];
    @Input() entity: HierarchyEntity;
    @Input() billingPeriod: BillingPeriod = null;
    @Input() billingPeriodCfg: BillingPeriodConfig = null;
    @Input() timeZone: string = null;
    @Input() hierarchyEntity: HierarchyEntity = null;
    @Output() selected: EventEmitter<SelectedAlgorithm> = new EventEmitter<SelectedAlgorithm>();

    year: number;
    form: FormGroup;
    template: Template = {name: null, file: null};
    step = 1;
    isAlgorithmSelected = false;
    templateDownloaded = false;
    templateChosen = false;
    validForm = false;
    chosenAlgorithm: string = null;
    enableCAP = false;
    showFanoWarning = null;
    allowFanoConfiguration = false;
    tip: number = null;
    fanoNetIncentive: number = null;

    readonly ALGORITHM_THOUSANDTH = ALGORITHM_THOUSANDTH;
    readonly ALGORITHM_VAI = ALGORITHM_VAI;
    readonly ALGORITHM_CAPPED_VAI = ALGORITHM_CAPPED_VAI;
    readonly ALGORITHM_MIXED = ALGORITHM_MIXED;
    readonly ALGORITHM_FANO = ALGORITHM_FANO;
    readonly ALGORITHM_CONDO_OLD = ALGORITHM_CONDO_OLD;
    readonly MAX_FILE_MB = MAX_FILE_MB;
    readonly XLSX_MIME_TYPE = XLSX_MIME_TYPE;
    readonly CACV_STRING = CACV_STRING;
    readonly isViewable = isViewable;
    readonly isUpdatable = isUpdatable;
    readonly limitDecimals = limitDecimals;

    constructor(private readonly fb: FormBuilder,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly apiService: SmagApiService) {
        this.form = this.fb.group({});
    }

    ngOnInit() {
        this.year = this.date.year();
        this.form.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => {
                const alg = {
                    type: this.chosenAlgorithm,
                    willNotRedistribute: value.setAsideTipUsage
                } as SelectedAlgorithm;
                if (this.chosenAlgorithm === ALGORITHM_VAI) {
                    alg.fixedPart = value.fixedPart / 100;
                } else if (this.chosenAlgorithm === ALGORITHM_CAPPED_VAI) {
                    alg.fixedPart = value.fixedPart / 100;
                    alg.globalCapEnabled = value.globalCapToggle;
                    alg.globalCap = isDefined(value.globalCap) ? value.globalCap / 100 : null;
                } else if (this.chosenAlgorithm === ALGORITHM_THOUSANDTH && this.templateChosen) {
                    alg.template = this.template;
                    alg.isThousandthsAlgo = true;
                } else if (this.chosenAlgorithm === ALGORITHM_MIXED && this.templateChosen) {
                    alg.template = this.template;
                } else if (this.chosenAlgorithm === ALGORITHM_FANO) {
                    alg.socialFundPercentage = value.socialFundPercentage / 100;
                    alg.foundersPercentage = value.foundersPercentage / 100;
                    alg.plantsPercentage = this.tip;
                } else if (this.chosenAlgorithm === ALGORITHM_CONDO_OLD) {
                    alg.tipFee = value.tipFee;
                    alg.tipBenefit = value.tipBenefit;
                    alg.cacvBenefit = value.cacvBenefit;
                    alg.withdrawalFee = value.withdrawalFeePercent / 100;
                    alg.withdrawalBenefit = value.withdrawalBenefitPercent / 100;
                }
                this.selectedAlgorithm = alg;
            });

        this.form.statusChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => this.validForm = this.form.valid);
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onSelectAlgorithm(algorithm: string) {
        this.isAlgorithmSelected = true;
        this.chosenAlgorithm = algorithm;

        if (this.chosenAlgorithm !== ALGORITHM_NO_ALGORITHM) {
            this.form.registerControl('setAsideTipUsage',
                new FormControl((this.response.surplus_premium_rate.scope !== ECSurplusPremiumRateScope.CONSUMER_REDISTRIBUTED),
                    [Validators.required]));
        }

        if (!this.registeredControls && (this.chosenAlgorithm === ALGORITHM_VAI || this.chosenAlgorithm === ALGORITHM_CAPPED_VAI)) {
            // ALGORITHM_VAI and ALGORITHM_CAPPED_VAI have a lot in common
            this.registeredControls = true;
            const redistribution = this.chosenAlgorithm === ALGORITHM_CAPPED_VAI ?
                this.response.redistribution as ECEconomicsCappedVAIRedistributionSettings :
                this.response.redistribution as ECEconomicsVAIRedistributionSettings;
            this.form.registerControl('fixedPart', new FormControl(
                isDefined(redistribution?.fixed_part) ? redistribution.fixed_part * 100 : null,
                Validators.compose([Validators.required, Validators.min(0), Validators.max(100)])));
            // controls only for ALGORITHM_CAPPED_VAI
            if (this.chosenAlgorithm === ALGORITHM_CAPPED_VAI) {
                const cappedRedistribution = this.response.redistribution as ECEconomicsCappedVAIRedistributionSettings;
                this.form.registerControl('globalCap', new FormControl(
                    cappedRedistribution?.global_cap ? cappedRedistribution.global_cap * 100 : null,
                    [Validators.min(0), Validators.max(100)]));
                this.form.registerControl('globalCapToggle', new FormControl(
                    cappedRedistribution?.global_cap_enabled ? cappedRedistribution.global_cap_enabled : false, []));
            }
            this.updateForm();
        } else if (!this.registeredControls && this.chosenAlgorithm === ALGORITHM_FANO) {
            this.registeredControls = true;
            const startTime = sanityFromTimeActivationDate(this.billingPeriod.fromTime, this.billingPeriodCfg.pastDate, this.timeZone);
            const endTime = sanityToTime(this.timeZone, this.billingPeriod.toTime);
            this.apiService.ecEconomicsPlantsPods(this.hierarchyEntity.id, startTime, endTime, 1, 999, '')
                .subscribe(plants => {
                    const isModeTip = plants.c_acv_mode === ECCacvRedistributionMode.AS_TIP;
                    const areAllTipsEqual = plants.pods.map(plant => plant.tip_configs?.plant_tip)
                        .every((tip, _, tips) => tip === tips[0]);
                    const areAllTipConfigsOk = plants.pods.map(plant => plant.tip_configs)
                        .every(plant => plant.mode === ECPlantTipMode.PERCENTAGE && plant.type === ECPlantTipType.NET);
                    this.showFanoWarning = !(isModeTip && areAllTipsEqual && areAllTipConfigsOk);
                    this.allowFanoConfiguration = areAllTipsEqual && areAllTipConfigsOk;
                    if (this.allowFanoConfiguration) {
                        const fanoRedistribution = this.response.redistribution as ECEconomicsFanoRedistributionSettings;
                        this.tip = plants.pods[0].tip_configs.plant_tip;
                        this.form.registerControl('foundersPercentage', new FormControl(isDefined(fanoRedistribution.founders_percentage) ? fanoRedistribution.founders_percentage * 100 : null,
                            Validators.compose([Validators.required, Validators.min(0), Validators.max(100)])));
                        this.form.registerControl('socialFundPercentage', new FormControl(isDefined(fanoRedistribution.social_fund_percentage) ? fanoRedistribution.social_fund_percentage * 100 : null,
                            Validators.compose([Validators.required, Validators.min(0), Validators.max(100)])));
                        this.form.setValidators((ctrl: AbstractControl) => this.validateFanoAlgorithm(ctrl));
                        if (isModeTip) {
                            this.fanoNetIncentive = this.response.incentivation.redistributable_revenue / (1 - this.tip);
                        } else {
                            const cacv = plants.pods.map(plant => plant.plant_c_acv).reduce((acc, val) => acc + val, 0);
                            const tips = plants.pods.map(plant => plant.plant_tip).reduce((acc, val) => acc + val, 0);
                            this.fanoNetIncentive = this.response.incentivation.redistributable_revenue + tips + cacv;
                        }
                    }
                });
        } else if (!this.registeredControls && this.chosenAlgorithm === ALGORITHM_CONDO_OLD) {
            this.registeredControls = true;
            // shared energy fee value -> tip fee
            // tip benefit value -> tip benefit
            // cacv benefit value -> cacv benefit
            // rei fee value -> withdrawal fee percent
            // sold energy benefit value -> withdrawal benefit percent
            const oldCondomRedistribution = this.response.redistribution as ECEconomicsOldCondoRedistributionSettings;
            this.form.registerControl('tipFee', new FormControl(oldCondomRedistribution.shared_energy_fee_value,
                [Validators.required, Validators.min(0), Validators.max(1000)]));
            this.form.registerControl('tipBenefit', new FormControl(oldCondomRedistribution.tip_benefit_value,
                [Validators.required, Validators.min(0), Validators.max(1000)]));
            this.form.registerControl('cacvBenefit', new FormControl(oldCondomRedistribution.cacv_benefit_value,
                [Validators.required, Validators.min(0), Validators.max(1000)]));
            this.form.registerControl('withdrawalFeePercent', new FormControl(oldCondomRedistribution.rei_fee_value * 100,
                [Validators.required, Validators.min(0), Validators.max(100)]));
            this.form.registerControl('withdrawalBenefitPercent', new FormControl(oldCondomRedistribution.sold_energy_benefit_value * 100,
                [Validators.required, Validators.min(0), Validators.max(100)]));
        } else if (this.chosenAlgorithm === ALGORITHM_THOUSANDTH || this.chosenAlgorithm === ALGORITHM_MIXED) {
            this.updateForm();
        } else if (this.chosenAlgorithm === ALGORITHM_NO_ALGORITHM) {
            this.selected.emit({type: this.chosenAlgorithm} as SelectedAlgorithm);
        }
    }

    onClickDownloadThousandthsTemplate() {
        this.apiService.getECAlgorithmFile(this.entity.id, EC_FILE_THOUSANDTHS, this.year,
            this.response.redistribution?.algorithm !== ALGORITHM_THOUSANDTH)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.templateDownloaded = true;
            });
    }

    onClickUploadTemplate(event: Event) {
        const tg = event.target as HTMLInputElement;
        if (tg.files && tg.files.length) {
            const file: File = tg.files[0];
            if (validateFileSize(file, this.i18n, this.msgBoxService)) {
                this.template = {name: file.name, file};
                this.templateChosen = true;
                this.updateForm();
            }
        }
    }

    onClickDownloadMixedTemplate() {
        this.apiService.getECAlgorithmFile(this.entity.id, EC_FILE_MIXED, this.year,
            this.response.redistribution?.algorithm !== ALGORITHM_MIXED)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.templateDownloaded = true;
            });
    }

    onClickCancel() {
        this.selected.emit(null);
    }

    onClickSave() {
        this.selected.emit(this.selectedAlgorithm);
    }

    onClickTrashTemplate() {
        this.template = {name: null, file: null};
        this.templateChosen = false;
    }

    onClickNextStep() {
        this.step++;
    }

    onClickGoToStep(newStep: number) {
        if (newStep) {
            this.step = newStep;
        }
    }

    goToStepIfValid(step: number, controlName: string): void {
        if (this.form.controls[controlName].valid) {
            this.onClickGoToStep(step);
        }
    }

    onCAPToggleEvent($event: MatSlideToggleChange) {
        this.enableCAP = $event.checked;
    }

    getFixedPart() {
        return this.response.incentivation.redistributable_revenue * this.form.controls.fixedPart.value / 100;
    }

    get remainingAmount() {
        return this.response.incentivation.redistributable_revenue - this.getFixedPart();
    }

    get performanceTotalPods() {
        return this.response.performance.statistical_pods + this.response.performance.field_pods;
    }

    get tooMuchErrorMessage() {
        return this.i18n.translate(
            'The sum of the percentages allocated to founding members ({0}%), social fund ({1}%) and tip ({2}%) must be < 100%',
            this.form.controls.foundersPercentage.value, this.form.controls.socialFundPercentage.value, this.tip * 100);
    }

    private updateForm() {
        for (const ctrl in this.form.controls) {
            if (this.form.controls.hasOwnProperty(ctrl)) {
                this.form.get(ctrl).updateValueAndValidity({onlySelf: true, emitEvent: true});
            }
        }
        this.form.updateValueAndValidity({onlySelf: true, emitEvent: true});
    }

    private validateFanoAlgorithm(ctrl: AbstractControl): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls = (ctrl as FormGroup).controls;
        const foundersPercentage = ctrls.foundersPercentage.value / 100;
        const socialFundPercentage = ctrls.socialFundPercentage.value / 100;
        if (foundersPercentage + socialFundPercentage + this.tip > 1) {
            errs.tooMuch = true;
        } else {
            errs.tooMuch = null;
        }
        return Object.values(errs).every(err => !err) ? null : errs;
    }

}
